import { useDebounce } from '@uidotdev/usehooks'
import { useQueryState } from 'nuqs'
import posthog from 'posthog-js'
import { useCallback, useDeferredValue, useEffect, useMemo } from 'react'
import { unstable_useRoute } from 'react-router'
import {
	type IconFormat,
	parseAsFormat,
	searchParamsParsers,
} from '#app/utils/search-params.js'

interface UseIconSetSearchOptions {
	iconSetIdentifier: string | null | undefined
	isAnonymous?: boolean
}

export function useDefaultFormat(): IconFormat {
	const rootData = unstable_useRoute('root')?.loaderData
	const domainKey = rootData?.domainConfig?.domainKey
	// Notion Icons defaults to copying a Notion-compatible link; all other brands default to raw SVG.
	return domainKey === 'notionicons' ? 'notion' : 'copy-svg'
}

export function useIconSetSearch({
	iconSetIdentifier,
	isAnonymous,
}: UseIconSetSearchOptions) {
	const defaultFormat = useDefaultFormat()

	const formatParser = useMemo(
		() => parseAsFormat.withDefault(defaultFormat),
		[defaultFormat],
	)

	const [query, setQueryState] = useQueryState(
		'query',
		searchParamsParsers.query,
	)
	const deferredQuery = useDeferredValue(query)
	const debouncedQuery = useDebounce(deferredQuery, 500)
	const [searchType, setSearchType] = useQueryState(
		'searchType',
		searchParamsParsers.searchType,
	)
	const [grouping, setGrouping] = useQueryState(
		'grouping',
		searchParamsParsers.grouping,
	)
	const [iconName, setIconName] = useQueryState(
		'iconName',
		searchParamsParsers.iconName,
	)
	const [format, setFormat] = useQueryState('format', formatParser)

	// PostHog analytics
	useEffect(() => {
		if (debouncedQuery) {
			posthog.capture('search', {
				query: debouncedQuery,
				iconSet: iconSetIdentifier ?? 'all',
				type: searchType,
				isAnonymous: isAnonymous ?? false,
			})
		}
	}, [debouncedQuery, iconSetIdentifier, searchType, isAnonymous])

	const setQuery = useCallback(
		(newQuery: string | null) => {
			void setQueryState(newQuery)
		},
		[setQueryState],
	)

	return {
		query,
		setQuery,
		deferredQuery,
		debouncedQuery,
		searchType,
		setSearchType,
		grouping,
		setGrouping,
		iconName,
		setIconName,
		format,
		setFormat,
	}
}
